<template>
  <form
    method="post"
    :class="$style.form"
    data-qa="applicant_export"
    @submit.prevent.stop="handleSubmit"
  >
    <input
      type="hidden"
      name="_xsrf"
      :value="_xsrf"
    >
    <input
      v-for="field in allSelectedFields"
      :key="field"
      type="hidden"
      name="field"
      :value="field"
    >
    <input
      v-for="status in selectedStatuses"
      :key="status"
      type="hidden"
      name="status"
      :value="status"
    >
    <dropdown-menu-layout
      :title="title"
      size="larger"
    >
      <template #main>
        <dropdown-autocomplete
          :value="selectedFields"
          multiple
          :items="fields"
          @change="selectedFields = $event"
        >
          <template #before>
            <vue-void />
          </template>
          <template #toggle="{ toggle, count }">
            <div
              class="form-group"
              data-qa="export_fields"
            >
              <base-label :class="$style.label">
                {{ $trlMessage('vacancy.stoplist.title') }}
              </base-label>
              <toggle-button @click="toggle">
                {{ $trlMessage('vacancy.stoplist.field.fio') }}
                <template v-if="count">
                  + {{ $trlMessage('applicant.export.fields.count', { count }) }}
                </template>
              </toggle-button>
            </div>
          </template>
        </dropdown-autocomplete>

        <dropdown-autocomplete
          v-if="additionalFields.length"
          :value="selectedAdditionalFields"
          multiple
          :items="additionalFields"
          @change="selectedAdditionalFields = $event"
        >
          <template #before>
            <vue-void />
          </template>
          <template #toggle="{ toggle, count, selectedItems }">
            <div
              :class="{ 'form-group': shouldShowStatusList }"
              data-qa="additional_fields"
            >
              <base-label :class="$style.label">
                {{ $trlMessage('applicant.export.additional.fields.label') }}
              </base-label>
              <toggle-button @click="toggle">
                <template v-if="count === 1">
                  {{ selectedItems[0].name }}
                </template>
                <template v-else>
                  {{ $trlMessage('applicant.export.additional.fields.count', { count }) }}
                </template>
              </toggle-button>
            </div>
          </template>
        </dropdown-autocomplete>

        <div v-if="shouldShowStatusList">
          <span :class="$style.statusesTitle">
            {{ $trlMessage('vacancy.stoplist.status.title') }}
          </span>
          <ul :class="$style.statuses">
            <li
              v-for="status in statuses"
              :key="status.id"
              :class="$style.status"
              data-qa="status"
            >
              <checkbox-field
                :label="status.name"
                :checked="(selectedStatuses || []).includes(status.id)"
                ellipsis
                @change-value="handleStatusChange(status.id, $event)"
              />
            </li>
          </ul>
          <span
            v-if="statusesError"
            :class="$style.error"
          >{{ statusesError }}</span>
        </div>
      </template>

      <template #error>
        <slot name="error" />
      </template>

      <template #footer>
        <base-button
          type="submit"
          color="black"
          :loading="loading"
          data-qa="download"
        >
          {{ $trlMessage('button.download') }}
        </base-button>
        <base-button
          data-qa="cancel"
          @click="handleCancel"
        >
          {{ $trlMessage('button.cancel') }}
        </base-button>
        <div
          v-if="limit"
          :class="$style.limitMsg"
        >
          {{ $trlMessage('Downloading search results limited to') }}&nbsp;{{ limit }}
        </div>
      </template>
    </dropdown-menu-layout>
  </form>
</template>

<script setup lang="ts">
import jsonSchemaTraverse from 'json-schema-traverse';
import { ref, computed, defineProps, defineEmits } from 'vue';
import { DropdownAutocomplete, ToggleButton } from '@/components/base-autocomplete';
import { normalizeSchemaPointer } from '@/components/vjsf/helpers/normalize-schema-pointer';
import { traverseUISchema } from '@/components/vjsf/helpers/traverse-ui-schema';
import { WIDGET_TYPE } from '@/components/vjsf/vjsf';
import type { RealStatus } from '@/shared/legacy/lib/status/status';
import type { AdditionalInfoSchemaType } from '@/shared/legacy/api/schema-converter';
import { SchemaConverterAPI } from '@/shared/legacy/api/schema-converter';
import { useI18N } from '@/shared/legacy/lib/use-i18n';
import BaseButton from '@/shared/legacy/ui/button/button.vue';
import DropdownMenuLayout from '@/shared/legacy/ui/dropdown-menu-layout/dropdown-menu-layout.vue';
import CheckboxField from '@/shared/legacy/ui/form-fields/checkbox/checkbox.vue';
import BaseLabel from '@/shared/legacy/ui/label/label.vue';
import VueVoid from '@/shared/legacy/ui/vue-void.vue';
import { XSRFHelper } from '@/shared/legacy/lib/util/xsrf-helper';
import { trlMessage } from '@/shared/lib/i18n';

const { statuses, value } = defineProps<PropsType>();
const emit = defineEmits<EmitsType>();
const i18n = useI18N();

type ValueType = {
  field: string[];
  status: Array<RealStatus['id']>;
};

type PropsType = {
  loading: boolean;
  statuses?: Array<RealStatus>;
  limit?: number;
  value?: Partial<ValueType>;
  title: string;
};

type EmitsType = {
  submit: [ValueType];
  cancel: [];
};

const fieldNames = ['email', 'phone', 'company', 'money', 'birthdate', 'status'];
const fields = fieldNames.map((id) => ({
  id,
  // eslint-disable-next-line no-restricted-syntax
  name: trlMessage(`stoplist.fields.${id}`)
}));
const additionalFields = ref<{
  id: string;
  name: string;
}[]>([]);

const selectedFields = ref<ValueType['field']>([]);
const selectedAdditionalFields = ref<ValueType['field']>([]);
const selectedStatuses = ref<ValueType['status']>(value?.status ?? []);
const statusesError = ref<string>();

const allSelectedFields = computed(() => {
  return [...selectedFields.value, ...selectedAdditionalFields.value];
});

const shouldShowStatusList = computed(() => {
  if (statuses) {
    return statuses.length > 0;
  }
  return false;
});

const handleSubmit = () => {
  if (shouldShowStatusList.value && selectedStatuses.value.length === 0) {
    statusesError.value = i18n('applicant.export.error.required-statuses');
    return;
  }
  emit('submit', {
    field: allSelectedFields.value,
    status: selectedStatuses.value
  });
};

const handleCancel = () => emit('cancel');
const handleStatusChange = (statusId: number, isChecked: boolean) => {
  selectedStatuses.value = isChecked
    ? [...selectedStatuses.value, statusId]
    : selectedStatuses.value.filter((id) => id !== statusId);
};

SchemaConverterAPI.getAdditionalInfoSchemaClient().then((result) => {
  additionalFields.value = getAdditionalFields(result);

  const filterSelected = (arr: { id: string }[]) => arr.map(({ id }) => id).filter((id) => value?.field?.includes(id));
  selectedFields.value = filterSelected(fields);
  selectedAdditionalFields.value = filterSelected(additionalFields.value);
});

const _xsrf = XSRFHelper.getToken();

/*
 * Получение списка полей доп. инфо в определённом схемой порядке.
 * Скрытые поля (на всякий случай, если они есть, но вообще не должны) не отображаем в списке.
 * Обход ui_schema делается для того, чтобы поля были упорядочены ('ui:order'),
 * обход schema для того, чтобы получить title
 */
function getAdditionalFields(additionalInfoSchema: AdditionalInfoSchemaType) {
  const uiFieldPointers: string[] = [];
  traverseUISchema(additionalInfoSchema.ui_schema, (field, pointer: string) => {
    if (field['ui:widget'] === WIDGET_TYPE.HIDDEN || field['ui:widget'] === WIDGET_TYPE.SUBMIT) {
      return;
    }
    uiFieldPointers.push(pointer);
  });
  const schemaFieldsMap = new Map();
  jsonSchemaTraverse(additionalInfoSchema.schema, {
    cb: {
      pre: (...args) => {
        const [schema, pointer] = args;
        const normalizedPointer = normalizeSchemaPointer(pointer);
        if (!schemaFieldsMap.has(normalizedPointer) && schema.title) {
          schemaFieldsMap.set(normalizedPointer, schema);
        }
      }
    }
  });
  return uiFieldPointers.filter((pointer) => schemaFieldsMap.has(pointer))
    .map((pointer) => ({
      // считаем названия полей уникальными, т.е. для id используем только "хвост"
      id: pointer.split('/').at(-1) as string,
      name: schemaFieldsMap.get(pointer).title as string
    }));
}
</script>

<i18n lang="json">
{
  "applicant.export.error.required-statuses": {
    "ru_RU": "Выберите хотя бы один этап подбора",
    "en_US": "Select at least one hiring status"
  }
}
</i18n>

<style module>
.form {
  border-radius: 8px;
}
.selectBtn {
  composes: full from '../../shared/legacy/ui/css/widths.module.css';
  composes: custom-select from global;
  composes: custom-select_triangle from global;
  display: block;
  padding-left: 15px;
  text-align: left;
}

.statusesTitle {
  display: block;
  margin-bottom: 12px;
}

.label {
  margin-bottom: 4px;
}

.statuses {
  list-style: none inside none;
  margin: 0;
  padding: 0;
}

.status {
  @mixin ellipsis;
  display: flex;
  &:not(:last-child) {
    margin-bottom: 12px;
  }
}

.error {
  composes: form-group__error from global;
  display: block;
}

.limitMsg {
  border-top: 1px solid $borderColor;
  margin-top: 8px;
  padding-top: 12px;
  font-size: 15px;
  line-height: 20px;
  color: #bbb;
  width: 100%;
}
</style>
